import { useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom, useStore } from "jotai";
import { useCallback, useEffect, useRef } from "react";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { LoadAccess } from "~/shared/schemas/shipment-management-schema.ts";

import {
	initialShipmentLockState,
	shipmentLockAtom,
} from "../state/shipment-lock-atoms.ts";
import { usePutLockShipment } from "./use-put-lock-shipment.ts";
import { unlockShipment, useUnlockShipment } from "./use-unlock-shipment.ts";

export const useShipmentLock = ({
	poNumber,
	enabled,
	shouldHoldLock,
	isShipmentLoaded,
	lock,
	currentUserId,
}: {
	poNumber: string;
	enabled: boolean;
	shouldHoldLock: boolean;
	isShipmentLoaded: boolean;
	lock: LoadAccess["lock"];
	currentUserId: string;
}) => {
	const store = useStore();
	const queryClient = useQueryClient();
	const setLockState = useSetAtom(shipmentLockAtom);
	const { lockShipmentAsync } = usePutLockShipment({
		showNotifications: false,
	});
	const { unlockShipmentAsync } = useUnlockShipment({
		showNotifications: false,
	});
	const { showWarning } = useShipmentNotifications();

	// Read the latest values via refs so the acquisition effect below runs
	// exactly once when the shipment first loads and is never torn down /
	// re-run by re-renders or shipment refetches — that churn is what caused a
	// spurious unlock-then-relock after we acquired an unlocked shipment.
	const lockRef = useRef(lock);
	lockRef.current = lock;
	const lockShipmentRef = useRef(lockShipmentAsync);
	lockShipmentRef.current = lockShipmentAsync;
	const unlockShipmentRef = useRef(unlockShipmentAsync);
	unlockShipmentRef.current = unlockShipmentAsync;
	const showWarningRef = useRef(showWarning);
	showWarningRef.current = showWarning;
	const queryClientRef = useRef(queryClient);
	queryClientRef.current = queryClient;

	// Guards the one-time acquisition (reset when leaving the shipment) and lets
	// the release effect signal an in-flight acquisition to abort.
	const initializedRef = useRef(false);
	const cancelledRef = useRef(false);

	// Keep lock ownership aligned with edit mode. When edit mode is off (or the
	// lock feature is disabled), release any lock acquired by this session.
	useEffect(() => {
		if (enabled && shouldHoldLock) {
			return;
		}

		const lockedByUserId = lockRef.current?.lockedByUserId ?? null;
		const lockedByOther =
			lockedByUserId != null && String(lockedByUserId) !== currentUserId;

		// Capture ownership before resetting state below clears hasLock — checking
		// the atom after setLockState would always see it as already unlocked.
		const hadLock = store.get(shipmentLockAtom).hasLock;

		cancelledRef.current = true;
		initializedRef.current = false;
		setLockState((prev) => ({
			...prev,
			hasLock: false,
			isLockedByOther: lockedByOther,
			isResolved: true,
		}));

		const releaseIfHeld = async () => {
			if (hadLock) {
				try {
					await unlockShipmentRef.current(poNumber);
				} catch {
					// Best-effort unlock; pagehide/unmount handlers still provide cleanup.
				}
			}
		};

		void releaseIfHeld();
	}, [enabled, shouldHoldLock, poNumber, currentUserId, setLockState, store]);

	// Acquire the lock once, when the shipment has loaded.
	useEffect(() => {
		if (
			!enabled ||
			!shouldHoldLock ||
			!poNumber ||
			!isShipmentLoaded ||
			!currentUserId ||
			initializedRef.current
		) {
			return;
		}

		// Keep the page "resolved" while attempting to acquire the edit lock so
		// entering edit mode does not trip the page-level loading gate and unmount
		// the screen (which would reset edit state).
		setLockState((prev) => ({
			...prev,
			isLockedByOther: false,
		}));

		initializedRef.current = true;
		cancelledRef.current = false;

		const lockedByUserId = lockRef.current?.lockedByUserId ?? null;
		const lockedByMe =
			lockedByUserId != null && String(lockedByUserId) === currentUserId;
		const lockedByOther = lockedByUserId != null && !lockedByMe;

		const resolveLock = async () => {
			// Someone else holds the lock — the page stays read-only.
			if (lockedByOther) {
				setLockState((prev) => ({
					...prev,
					isLockedByOther: true,
					isResolved: true,
				}));
				showWarningRef.current(
					"This shipment is currently being edited by another user. Actions are disabled.",
				);
				return;
			}

			try {
				// No one holds the lock yet — acquire it. If it is already ours we
				// keep it without re-locking.
				if (lockedByUserId == null) {
					await lockShipmentRef.current(poNumber);
				}

				if (cancelledRef.current) {
					// Left the page before acquisition resolved — release what we took.
					if (lockedByUserId == null) {
						await unlockShipmentRef.current(poNumber);
					}
					return;
				}

				setLockState((prev) => ({
					...prev,
					hasLock: true,
					isLockedByOther: false,
					isResolved: true,
				}));

				// We just acquired a previously-unlocked shipment. Refetch so the
				// shipment's loadAccess.lock reflects our ownership.
				if (lockedByUserId == null) {
					void queryClientRef.current.invalidateQueries({
						queryKey: ["shipment"],
					});
				}
			} catch {
				if (!cancelledRef.current) {
					setLockState((prev) => ({
						...prev,
						isLockedByOther: true,
						isResolved: true,
					}));
					showWarningRef.current(
						"This shipment is currently being edited by another user. Actions are disabled.",
					);
				}
			}
		};

		void resolveLock();
	}, [
		enabled,
		shouldHoldLock,
		poNumber,
		isShipmentLoaded,
		currentUserId,
		setLockState,
	]);

	// Release the lock only when leaving the shipment (unmount or PO change),
	// never on a re-render. This keeps acquisition and release independent so a
	// refetch can never trigger an unlock/relock cycle.
	useEffect(() => {
		return () => {
			cancelledRef.current = true;

			if (store.get(shipmentLockAtom).hasLock) {
				void unlockShipmentRef.current(poNumber);
			}

			initializedRef.current = false;
			setLockState(initialShipmentLockState);
		};
	}, [poNumber, store, setLockState]);

	// Release the lock when the whole document is torn down — tab/window close,
	// hard refresh, or navigating away to another site. React's unmount cleanup
	// above never runs in those cases, so we listen for `pagehide` and fire a
	// keepalive DELETE (which the browser keeps in flight as the page unloads).
	useEffect(() => {
		const handlePageHide = (event: PageTransitionEvent) => {
			// `persisted` means the page is being frozen into the bfcache, not
			// destroyed — the user may restore and resume editing, so keep the lock.
			if (event.persisted) {
				return;
			}
			if (store.get(shipmentLockAtom).hasLock) {
				void unlockShipment({ poNumber, keepalive: true });
			}
		};

		window.addEventListener("pagehide", handlePageHide);
		return () => window.removeEventListener("pagehide", handlePageHide);
	}, [poNumber, store]);
};

export const useShipmentLockState = () => useAtomValue(shipmentLockAtom);

export const useForceUnlockShipment = (poNumber: string) => {
	const setLockState = useSetAtom(shipmentLockAtom);

	const { unlockShipmentAsync } = useUnlockShipment({
		showNotifications: false,
	});
	const { showSuccess, showError } = useShipmentNotifications();

	return useCallback(async () => {
		setLockState((prev) => ({ ...prev, isForcingUnlock: true }));

		try {
			await unlockShipmentAsync(poNumber);
			setLockState((prev) => ({
				...prev,
				hasLock: false,
				isLockedByOther: false,
				isForcingUnlock: false,
				wasForceUnlocked: true,
			}));
			showSuccess(SHIPMENT_TOAST_MESSAGES.shipmentForceUnlocked);
		} catch {
			setLockState((prev) => ({ ...prev, isForcingUnlock: false }));
			showError("Failed to unlock shipment. Please try again.");
		}
	}, [poNumber, setLockState, unlockShipmentAsync, showSuccess, showError]);
};
